import { Component, type ErrorInfo, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AppLayout } from "../layout/AppLayout";
import { useAuth } from "../auth/AuthProvider";
import { LoadingState } from "../../components/feedback/AsyncState";
import { LoginPage } from "../../features/auth/LoginPage";
import { InvitationAcceptPage } from "../../features/auth/InvitationAcceptPage";
import { CampaignDetailPage } from "../../features/campaigns/CampaignDetailPage";
import { CampaignsPage } from "../../features/campaigns/CampaignsPage";
import { ChannelsPage } from "../../features/channels/ChannelsPage";
import { PresetsPage } from "../../features/presets/PresetsPage";
import { WorkspacesPage } from "../../features/workspaces/WorkspacesPage";
import { WorkspaceDetailPage } from "../../features/workspaces/WorkspaceDetailPage";
import { DiagnosticsPage } from "../../features/diagnostics/DiagnosticsPage";
import { ProvidersPage } from "../../features/providers/ProvidersPage";
import { ProviderConnectionsPage } from "../../features/providers/ProviderConnectionsPage";
import { CostsPage } from "../../features/costs/CostsPage";
import { IdeasPage } from "../../features/ideas/IdeasPage";
import { ResearchPage } from "../../features/research/ResearchPage";
import { PromptsPage } from "../../features/prompts/PromptsPage";
import { StoryboardPage } from "../../features/storyboard/StoryboardPage";
import { AssetsPage } from "../../features/assets/AssetsPage";
import { VoicePage } from "../../features/voice/VoicePage";
import { RenderTemplatesPage } from "../../features/render-templates/RenderTemplatesPage";
import { CaptionsPage } from "../../features/captions/CaptionsPage";
import { CaptionStylesPage } from "../../features/captions/CaptionStylesPage";
import { AudioMixPage } from "../../features/audio-mix/AudioMixPage";
import { ThumbnailStudioPage } from "../../features/thumbnails/ThumbnailStudioPage";
import { ThumbnailTemplatesPage } from "../../features/thumbnails/ThumbnailTemplatesPage";
import { QualityReviewPage } from "../../features/quality-review/QualityReviewPage";
import { MetadataPackagePage } from "../../features/metadata/MetadataPackagePage";
import { ComplianceReviewPage } from "../../features/compliance/ComplianceReviewPage";
import { PrivacyPage } from "../../features/privacy/PrivacyPage";
import { SecurityEventsPage } from "../../features/security/SecurityEventsPage";
import { YouTubeConnectionsPage } from "../../features/youtube/connections/YouTubeConnectionsPage";
import { YouTubeUploadsPage } from "../../features/youtube/uploads/YouTubeUploadsPage";

export function AppRouter(): React.JSX.Element {
  return <BrowserRouter><Routes>
    <Route path="/login" element={<LoginRoute />} />
    <Route path="/invitations/:token/accept" element={<InvitationRoute />} />
    <Route path="*" element={<AuthenticatedRoutes />} />
  </Routes></BrowserRouter>;
}

function LoginRoute(): React.JSX.Element {
  const { state } = useAuth();
  if (state === "loading") return <LoadingState label="Restoring secure session…" />;
  return state === "authenticated" ? <Navigate to="/campaigns" replace /> : <LoginPage />;
}

function InvitationRoute(): React.JSX.Element {
  const { token = "" } = useParams();
  return <InvitationAcceptPage token={token} />;
}

function AuthenticatedRoutes(): React.JSX.Element {
  const { state } = useAuth();
  const location = useLocation();
  if (state === "loading") return <LoadingState label="Restoring secure session…" />;
  if (state === "anonymous") return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <AppLayout><ErrorBoundary><Routes>
    <Route path="/" element={<Navigate to="/campaigns" replace />} />
    <Route path="/campaigns" element={<CampaignsPage />} />
    <Route path="/campaigns/:campaignId" element={<CampaignRoute />} />
    <Route path="/outputs/:outputId/storyboard" element={<StoryboardRoute />} />
    <Route path="/outputs/:outputId/captions" element={<CaptionsRoute />} />
    <Route path="/outputs/:outputId/audio-mix" element={<AudioMixRoute />} />
    <Route path="/outputs/:outputId/thumbnails" element={<ThumbnailRoute />} />
    <Route path="/outputs/:outputId/quality-review" element={<QualityReviewRoute />} />
    <Route path="/outputs/:outputId/metadata" element={<MetadataPackageRoute />} />
    <Route path="/outputs/:outputId/compliance" element={<ComplianceReviewRoute />} />
    <Route path="/outputs/:outputId/uploads" element={<YouTubeUploadRoute />} />
    <Route path="/ideas" element={<IdeasPage />} />
    <Route path="/research" element={<ResearchPage />} />
    <Route path="/prompts" element={<PromptsPage />} />
    <Route path="/channels" element={<ChannelsPage />} />
    <Route path="/presets" element={<PresetsPage />} />
    <Route path="/assets" element={<AssetsPage />} />
    <Route path="/voice" element={<VoicePage />} />
    <Route path="/youtube/connections" element={<YouTubeConnectionsPage />} />
    <Route path="/settings/render-templates" element={<RenderTemplatesPage />} />
    <Route path="/settings/caption-styles" element={<CaptionStylesPage />} />
    <Route path="/settings/thumbnail-templates" element={<ThumbnailTemplatesPage />} />
    <Route path="/settings/security-events" element={<SecurityEventsPage />} />
    <Route path="/settings/privacy" element={<PrivacyPage />} />
    <Route path="/providers" element={<ProvidersPage />} />
    <Route path="/settings/providers" element={<ProviderConnectionsPage />} />
    <Route path="/pricing" element={<CostsPage />} />
    <Route path="/workspaces" element={<WorkspacesPage />} />
    <Route path="/workspaces/:workspaceId" element={<WorkspaceRoute />} />
    <Route path="/diagnostics" element={<DiagnosticsPage />} />
    <Route path="*" element={<NotFound />} />
  </Routes></ErrorBoundary></AppLayout>;
}

function CampaignRoute(): React.JSX.Element {
  const { campaignId = "" } = useParams();
  return <CampaignDetailPage campaignId={campaignId} />;
}

function StoryboardRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <StoryboardPage outputId={outputId} />;
}

function CaptionsRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <CaptionsPage outputId={outputId} />;
}

function AudioMixRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <AudioMixPage outputId={outputId} />;
}

function ThumbnailRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <ThumbnailStudioPage outputId={outputId} />;
}

function QualityReviewRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <QualityReviewPage outputId={outputId} />;
}

function MetadataPackageRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <MetadataPackagePage outputId={outputId} />;
}

function ComplianceReviewRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <ComplianceReviewPage outputId={outputId} />;
}

function YouTubeUploadRoute(): React.JSX.Element {
  const { outputId = "" } = useParams();
  return <YouTubeUploadsPage outputId={outputId} />;
}

function WorkspaceRoute(): React.JSX.Element {
  const { workspaceId = "" } = useParams();
  return <WorkspaceDetailPage workspaceId={workspaceId} />;
}

function NotFound(): React.JSX.Element {
  return <section className="state state-error"><h1>Page not found</h1><p>The requested operator route does not exist.</p><a className="button-secondary" href="/campaigns">Back to campaigns</a></section>;
}

class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } { return { failed: true }; }
  componentDidCatch(error: Error, info: ErrorInfo): void { void error; void info; /* Browser errors are not logged with sensitive data. */ }
  render(): ReactNode { return this.state.failed ? <section className="state state-error"><h1>Console view unavailable</h1><p>Reload the current route to restore the operator view.</p><button type="button" onClick={() => window.location.reload()}>Reload</button></section> : this.props.children; }
}
